import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { isChromeBasedBrowser, isMobileDevice } from '../../../utils';
import { useAuthHandlers } from '../../../services/actions/auth/useAuthHandlers';
import { useExternalHandlers } from '../../../services/actions/navigation/useExternalHandlers';
import { useAppActions } from '../../../stores/application/useAppActions';
import { useOnboardingExtensionInstallDetect } from '../hooks/useOnboardingExtensionInstallDetect';
import { AppRoutes } from '../../../interfaces/store/appStore';
import { styles } from '../../../styles/themes/style';
import SvgFragment from '../../../fragments/SvgFragment';
import { ChromeOnboardingIcon, LeadDeltaOnboardingIcon, OnboardingConnectionIconRow, OnboardingLinkIcon } from '../components/OnboardingConnectionIcons';
import OnboardingStepLayout from '../components/OnboardingStepLayout';
import { clearSkippedToAllSet } from '../onboardingExtensionSkipUtils';
import { AuthOnboardingCard, AuthOnboardingCardBody, AuthOnboardingIntro, AuthInfoBox, AuthMethodButton, AuthSkipLink, AuthOnboardingActions, AuthOnboardingTrustSection, AuthOnboardingTrustDivider, AuthTrustRow, AuthTrustItem } from '../../authentication/authFormCommon';

interface IExtensionInstallProps {}

const CARD_SUBTITLE = 'Takes less than a minute. You will be redirected back here after installation.';

const PAGE_SUBTITLE = 'The Chrome App is the safest way to import your LinkedIn connections and inbox and keep them in sync. Without it you can still explore the product, but your network won’t load.';

const PERMISSIONS_COPY = 'Next, Chrome will ask to “read and change your data on all websites.” That sounds broad, so here’s the honest reason: LeadDelta’s right-click templates let you drop saved messages into any text field — your email, your CRM, anywhere you write, not just LinkedIn. The extension never reads, tracks, or stores your browsing.';

const SKIP_CONFIRM_TITLE = 'Are you sure you want to continue without LeadDelta Chrome App?';

const SKIP_WARNING = 'You can explore LeadDelta without the app, but your contacts will not sync.';

const ExtensionInstall: FunctionComponent<IExtensionInstallProps> = () => {
    const { saveOnboardingData } = useAuthHandlers();
    const { visitChromeStore } = useExternalHandlers();
    const { setOnboardingStep } = useAppActions();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);

    useOnboardingExtensionInstallDetect(true);

    useEffect(() => {
        if (!isChromeBasedBrowser() && !isMobileDevice()) {
            navigate(AppRoutes.ONBOARDING_UNSUPPORTED_BROWSER, { replace: true });
        }
    }, [navigate]);

    const skipExtensionInstalationClick = useCallback(async () => {
        setLoading(true);
        try {
            clearSkippedToAllSet();
            setOnboardingStep(4);
            await saveOnboardingData({
                startedExtensionInstallation: false,
            });
            setOnboardingStep(4);
            navigate(AppRoutes.ONBOARDING_SURVEY);
        } finally {
            setLoading(false);
        }
    }, [saveOnboardingData, setOnboardingStep, navigate]);

    const visitChromeStoreClick = useCallback(async () => {
        await Promise.all([
            saveOnboardingData({
                startedExtensionInstallation: true,
            }),
            (async () => {
                try {
                    sessionStorage.setItem('ld_extension_install_source', 'onboarding');
                } catch {
                    /* ignore */
                }
                return visitChromeStore();
            })(),
        ]);
    }, [saveOnboardingData, visitChromeStore]);

    if (step === 1) {
        return (
            <OnboardingStepLayout
                activeStepIndex={0}
                title="Install LeadDelta Chrome App"
                subtitle={CARD_SUBTITLE}
            >
                <AuthOnboardingCard>
                    <OnboardingConnectionIconRow>
                        <LeadDeltaOnboardingIcon aria-hidden />
                        <OnboardingLinkIcon />
                        <ChromeOnboardingIcon aria-hidden />
                    </OnboardingConnectionIconRow>

                    <AuthOnboardingCardBody>
                        <AuthOnboardingIntro>{PAGE_SUBTITLE}</AuthOnboardingIntro>

                        <AuthInfoBox>
                            <p>{PERMISSIONS_COPY}</p>
                        </AuthInfoBox>

                        <AuthOnboardingActions>
                            <AuthMethodButton
                                $type="blue"
                                $SVGtype="openNewTab"
                                onClickHandler={() => visitChromeStoreClick()}
                            >
                                Install Chrome App
                            </AuthMethodButton>
                            <AuthSkipLink
                                type="button"
                                onClick={() => setStep(2)}
                            >
                                Continue without app
                            </AuthSkipLink>
                        </AuthOnboardingActions>

                        <AuthOnboardingTrustSection>
                            <AuthOnboardingTrustDivider />
                            <AuthTrustRow>
                                <AuthTrustItem>
                                    <TrustLinkedInIcon />
                                    Works on LinkedIn
                                </AuthTrustItem>
                                <AuthTrustItem>
                                    <TrustShieldIcon />
                                    We never sell your data
                                </AuthTrustItem>
                                <AuthTrustItem>
                                    <TrustExportIcon />
                                    Export or delete anytime
                                </AuthTrustItem>
                            </AuthTrustRow>
                        </AuthOnboardingTrustSection>
                    </AuthOnboardingCardBody>
                </AuthOnboardingCard>
            </OnboardingStepLayout>
        );
    }

    return (
        <OnboardingStepLayout activeStepIndex={0} title={SKIP_CONFIRM_TITLE}>
            <ConfirmStepContent>
                <AuthOnboardingCard>
                    <AuthOnboardingCardBody>
                        <AuthOnboardingIntro>{SKIP_WARNING}</AuthOnboardingIntro>

                        <AuthOnboardingActions>
                            <AuthMethodButton
                                $type="blue"
                                $SVGtype="openNewTab"
                                disabled={loading}
                                onClickHandler={() => visitChromeStoreClick()}
                            >
                                Install Chrome app
                            </AuthMethodButton>
                            <AuthSkipLink
                                type="button"
                                disabled={loading}
                                onClick={() => void skipExtensionInstalationClick()}
                            >
                                Continue anyway
                            </AuthSkipLink>
                        </AuthOnboardingActions>
                    </AuthOnboardingCardBody>
                </AuthOnboardingCard>
            </ConfirmStepContent>
        </OnboardingStepLayout>
    );
};

const TrustLinkedInIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.6667 7.33301H3.33333C2.59695 7.33301 2 7.92996 2 8.66634V13.333C2 14.0694 2.59695 14.6663 3.33333 14.6663H12.6667C13.403 14.6663 14 14.0694 14 13.333V8.66634C14 7.92996 13.403 7.33301 12.6667 7.33301Z" stroke="#545E77" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M4.66666 7.33301V4.66634C4.66666 3.78229 5.01785 2.93444 5.64297 2.30932C6.26809 1.6842 7.11593 1.33301 7.99999 1.33301C8.88404 1.33301 9.73189 1.6842 10.357 2.30932C10.9821 2.93444 11.3333 3.78229 11.3333 4.66634V7.33301" stroke="#545E77" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const TrustShieldIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_11221_76628)">
            <path d="M8.00001 14.6663C11.6819 14.6663 14.6667 11.6816 14.6667 7.99967C14.6667 4.31778 11.6819 1.33301 8.00001 1.33301C4.31811 1.33301 1.33334 4.31778 1.33334 7.99967C1.33334 11.6816 4.31811 14.6663 8.00001 14.6663Z" stroke="#545E77" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M3.28601 3.28613L12.7133 12.7141" stroke="#545E77" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </g>
        <defs>
            <clipPath id="clip0_11221_76628">
            <rect width="16" height="16" fill="white"/>
            </clipPath>
        </defs>
    </svg>
);

const TrustExportIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 10V2" stroke="#545E77" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10" stroke="#545E77" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M4.66669 6.66699L8.00002 10.0003L11.3334 6.66699" stroke="#545E77" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
);

const ConfirmStepContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 28px;
    width: 100%;
`;

const BenefitBoxRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 16px;
    width: 100%;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const BenefitBox = styled.div`
    box-sizing: border-box;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 24px;
    gap: 8px;
    border: 1px solid ${styles.colors.black200};
    border-radius: 12px;
    background: ${styles.colors.white};
`;

const BenefitIconWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-bottom: 4px;

    .path {
        fill: ${styles.colors.primary600};
    }
`;

const BenefitTitle = styled.span`
    font-family: NunitoSans;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    color: ${styles.colors.black600};
`;

const BenefitDescription = styled.span`
    font-family: NunitoSans;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    color: ${styles.colors.black400};
`;

export default ExtensionInstall;
